import { ObjectId } from 'bson';
import { runInAction } from 'mobx';
import { Observer, observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal, ISegment } from '@feathr/blackbox';
import { ensurePredicateKeys } from '@feathr/blackbox';
import { Fieldset, Input, UniversalSelect } from '@feathr/components';
import ConversionPixelCode from '@feathr/extender/App/DataConversionPixelsPage/ConversionPixelCode';
import ConversionModelRadios from '@feathr/extender/components/ConversionModelRadios/ConversionModelRadios';
import { useFlags, useStore } from '@feathr/extender/state';

interface ICreateSegmentOption {
  id: 'create';
  name: string;
}

interface IProps {
  goal: Goal;
  disabled?: boolean;
}

function AdvancedConversionTrackingSettings({ goal, disabled = false }: IProps): JSX.Element {
  const { Segments } = useStore();
  const { t } = useTranslation();
  const flags = useFlags();

  const segments = Segments.list({
    filters: {
      is_conversion_segment: true,
    },
  });

  function createSegment(): void {
    segments.models.forEach((s) => {
      if (s.isEphemeral) {
        runInAction(() => {
          segments.models.remove(s);
        });
      }
    });
    const segmentId = new ObjectId().toHexString();
    const newSegment = Segments.create({
      id: segmentId,
      read_only: true,
      lookback_mode: 'unbounded',
      predicates: [
        ensurePredicateKeys({
          attr_type: 'string',
          attr_against: 'seg_id',
          comparison: 'eq',
          kind: 'activity',
          value: segmentId,
        }),
      ],
      is_conversion_segment: true,
    });
    runInAction(() => {
      segments.models.push(newSegment);
    });
    goal.set({ name: newSegment.name, segment: segmentId });
  }

  const segmentId = goal.get('segment');
  const segment = segmentId ? Segments.get(segmentId) : undefined;

  function handleChangePixelName(newValue?: string): void {
    segment!.set({ name: newValue });
    goal.set({ name: newValue });
  }

  function handleClearSegment(): void {
    goal.set({ name: '', segment: undefined });
  }

  function handleSelectSegment(option: ISegment | ICreateSegmentOption): void {
    if (option.id === 'create') {
      createSegment();
    } else {
      goal.set({ name: option.name, segment: option.id });
    }
  }

  const options: Array<ISegment | ICreateSegmentOption> = [
    { id: 'create', name: t('Create New Conversion Pixel') },
    ...segments.models.map((s) => s.toJS()),
  ];

  return (
    <>
      <Fieldset>
        <Observer>
          {(): JSX.Element => {
            return (
              <UniversalSelect<ISegment | ICreateSegmentOption>
                disabled={disabled}
                isClearable={true}
                label={t('Select or Create Conversion Pixel')}
                menuPosition={'fixed'}
                name={'conversion-pixel'}
                onClear={handleClearSegment}
                onSelectSingle={handleSelectSegment}
                options={options}
                value={options.find((option) => option.id === goal.get('segment'))}
              />
            );
          }}
        </Observer>
        {segment && segment.isEphemeral && (
          <Observer>
            {(): JSX.Element => (
              <Input
                helpText={t('Describe what this pixel is capturing. E.g., "Event Registrations"')}
                label={t('Conversion Pixel Name')}
                onChange={handleChangePixelName}
                type={'text'}
                value={segment.get('name')}
              />
            )}
          </Observer>
        )}
        {flags.hasMultiConversions && segment && <ConversionModelRadios.V1 goal={goal} />}
      </Fieldset>
      {segment && <ConversionPixelCode segment={segment} />}
    </>
  );
}

export default observer(AdvancedConversionTrackingSettings);
